import { ChangeDetectionStrategy, Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { IArrivedEarlyValidator, IArrivedLateValidator, ILeftEarlyValidator, ILeftLateValidator, INoShiftOnDateValidator, ITimeOnShiftExceedsWorkTime, ITimeOnShiftInsufficient, IValidationErrors } from './mes-shift.service';

interface ErrorMessage {
	translation: string;
	params?: object;
}

@Component({
	selector: 'app-shift-status',
	templateUrl: './mes-shift-status.component.html',
	styleUrls: ['./mes-shift-status.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class ShiftStatusComponent implements OnInit {
	public showPopup: boolean;
	public popupLeft: string;
	public popupTop: string;
	public arrivedEarly: boolean;
	public arrivedLate: boolean;
	public arrivedOnTime: boolean;
	public leftEarly: boolean;
	public leftLate: boolean;
	public leftOnTime: boolean;
	public noShiftOnDate: boolean;
	public notAWorkday: boolean;
	public shiftNotEnded: boolean;
	public exceeds: boolean;
	public insufficient: boolean;

	public errorMessages: ErrorMessage[];
	@Input() errors: IValidationErrors | null;
	@ViewChild('popup') popup: ElementRef<HTMLDivElement>;
	public constructor() {}

	public ngOnInit(): void {
		this.arrivedEarly = Boolean(this.errors?.arrivedEarlyValidator);
		this.arrivedLate = Boolean(this.errors?.arrivedLateValidator);
		this.leftEarly = Boolean(this.errors?.leftEarlyValidator);
		this.leftLate = Boolean(this.errors?.leftLateValidator);
		this.exceeds = Boolean(this.errors?.timeOnShiftExceedsWorkTimeValidator);
		this.shiftNotEnded = Boolean(this.errors?.shiftNotEndedValidator);
		this.noShiftOnDate = Boolean(this.errors?.noShiftOnDateValidator);
		this.notAWorkday = Boolean(this.errors?.notAWorkdayValidator);
		this.insufficient = Boolean(this.errors?.timeOnShiftInsufficientValidator);

		this.arrivedOnTime = !this.arrivedEarly && !this.arrivedLate && !this.noShiftOnDate;
		this.leftOnTime = !this.leftEarly && !this.leftLate && !this.noShiftOnDate && !this.shiftNotEnded;
		this.setErrorMessages();
	}

	private setErrorMessages(): void {
		this.errorMessages = [];

		if (this.arrivedEarly) {
			this.errorMessages.push(this.getArrivedEarlyOrLateErrorMessage('arrived-early', this.errors?.arrivedEarlyValidator));
		}
		if (this.arrivedLate) {
			this.errorMessages.push(this.getArrivedEarlyOrLateErrorMessage('arrived-late', this.errors?.arrivedLateValidator));
		}
		if (this.leftEarly) {
			this.errorMessages.push(this.getLeftEarlyOrLateErrorMessage('left-early', this.errors?.leftEarlyValidator));
		}
		if (this.leftLate) {
			this.errorMessages.push(this.getLeftEarlyOrLateErrorMessage('left-late', this.errors?.leftLateValidator));
		}
		if (this.noShiftOnDate) {
			this.errorMessages.push(this.getNoShiftOnDateMessage(this.errors?.noShiftOnDateValidator));
		}
		if (this.notAWorkday) {
			this.errorMessages.push(this.getErrorMessageFromKey('not-a-work-day'));
		}
		if (this.shiftNotEnded) {
			this.errorMessages.push(this.getErrorMessageFromKey('shift-not-ended'));
		}
		if (this.exceeds) {
			this.errorMessages.push(this.getExceedsOrInsufficientErrorMessage('exceeds-worktime', this.errors?.timeOnShiftExceedsWorkTimeValidator));
		}
		if (this.insufficient) {
			this.errorMessages.push(this.getExceedsOrInsufficientErrorMessage('insufficient-worktime', this.errors?.timeOnShiftInsufficientValidator));
		}
		if (!this.hasAnyErrors) {
			this.errorMessages.push(this.getErrorMessageFromKey('has-worked-as-expected'));
		}
	}

	public get hasAnyErrors(): boolean {
		return this.arrivedEarly || this.arrivedLate || this.leftEarly || this.leftLate || this.exceeds || this.shiftNotEnded || this.noShiftOnDate || this.notAWorkday || this.insufficient;
	}

	private getArrivedEarlyOrLateErrorMessage(key: string, error: IArrivedEarlyValidator | IArrivedLateValidator): ErrorMessage {
		return {
			translation: key,
			params: {
				actualStartTime: this.getIntlFormat(error.actualStartTime),
				expectedStartTime: this.getIntlFormat(error.expectedStartTime),
			},
		};
	}

	private getLeftEarlyOrLateErrorMessage(key: string, error: ILeftEarlyValidator | ILeftLateValidator): ErrorMessage {
		return {
			translation: key,
			params: {
				actualEndTime: this.getIntlFormat(error.actualEndTime),
				expectedEndTime: this.getIntlFormat(error.expectedEndTime),
			},
		};
	}

	private getNoShiftOnDateMessage(error: INoShiftOnDateValidator): ErrorMessage {
		return {
			translation: 'no-shift-on-date',
			params: {
				expectedStart: this.getIntlFormat(error.expectedStart),
				expectedEnd: this.getIntlFormat(error.expectedEnd),
			},
		};
	}

	private getErrorMessageFromKey(key: string): ErrorMessage {
		return {
			translation: key,
		};
	}

	private getExceedsOrInsufficientErrorMessage(key: string, error: ITimeOnShiftExceedsWorkTime | ITimeOnShiftInsufficient): ErrorMessage {
		const expected = this.getDuration(error.expectedWorkTime);
		const actual = this.getDuration(error.actualWorkTime);
		return {
			translation: key,
			params: {
				expectedHours: expected.hours,
				expectedMinutes: expected.minutes,
				actualHours: actual.hours,
				actualMinutes: actual.minutes,
			},
		};
	}

	private getIntlFormat(dateString: string): string {
		return new Intl.DateTimeFormat('en-GB', {
			month: 'short',
			day: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
		}).format(new Date(dateString));
	}

	private getDuration(value: string): { hours: number; minutes: number } {
		const hours = Number.parseInt(value.substring(0, 2));
		const minutes = Number.parseInt(value.substring(3, 5));
		return {
			hours,
			minutes,
		};
	}

	public pointerEnter(): void {
		this.showPopup = true;
	}

	public pointerMove(event: PointerEvent): void {
		this.updatePopupPosition(event);
	}

	public pointerOut(): void {
		this.showPopup = false;
	}

	private updatePopupPosition(event: PointerEvent): void {
		if (this.popup && this.popup.nativeElement) {
			const bounds = this.popup.nativeElement.getBoundingClientRect();
			this.popup.nativeElement.style.left = `${event.clientX - bounds.width}px`;
			this.popup.nativeElement.style.top = `${event.clientY + 24}px`;
		}
	}
}
